import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Shield, Smartphone, Key, AlertTriangle, CheckCircle2, ChevronRight, Copy, Loader2, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import { authApi, dashboardApi } from '@/services/api'

export function SecuritySettingsPage() {
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [user, setUser] = useState(null)

    // 2FA Setup State
    const [setupMode, setSetupMode] = useState(false)
    const [setupStep, setSetupStep] = useState(1) // 1: QR, 2: Backup Codes
    const [qrCodeData, setQrCodeData] = useState(null)
    const [secretKey, setSecretKey] = useState('')
    const [verificationCode, setVerificationCode] = useState('')
    const [backupCodes, setBackupCodes] = useState([])

    // Disable State
    const [disableMode, setDisableMode] = useState(false)

    // Fetch current user status on mount
    useEffect(() => {
        fetchUserData()
    }, [])

    const fetchUserData = async () => {
        try {
            const response = await dashboardApi.getProfile()
            setUser(response.data)
        } catch (error) {
            console.error('Failed to fetch user:', error)
            toast.error('Could not load security settings')
        } finally {
            setLoading(false)
        }
    }

    const handleStartSetup = async () => {
        setActionLoading(true)
        try {
            const response = await authApi.enable2FA()
            setQrCodeData(response.data.qr_code)
            setSecretKey(response.data.secret)
            setSetupMode(true)
            setSetupStep(1)
        } catch (error) {
            toast.error(error.response?.data?.msg || 'Failed to start 2FA setup')
        } finally {
            setActionLoading(false)
        }
    }

    const handleVerifySetup = async (e) => {
        e?.preventDefault()
        if (!verificationCode || verificationCode.length !== 6) {
            toast.error('Please enter a valid 6-digit code')
            return
        }

        setActionLoading(true)
        try {
            const response = await authApi.verify2FASetup(verificationCode)
            setBackupCodes(response.data.backup_codes)
            setSetupStep(2)
            toast.success('Two-factor authentication enabled successfully')

            // Update local user state
            setUser({ ...user, is_2fa_enabled: true })
        } catch (error) {
            toast.error(error.response?.data?.msg || 'Invalid verification code')
        } finally {
            setActionLoading(false)
        }
    }

    const handleDisable2FA = async (e) => {
        e?.preventDefault()
        if (!verificationCode || verificationCode.length !== 6) {
            toast.error('Please enter a valid 6-digit code')
            return
        }

        setActionLoading(true)
        try {
            await authApi.disable2FA(verificationCode)
            setDisableMode(false)
            toast.success('Two-factor authentication disabled')
            setVerificationCode('')

            // Update local user state
            setUser({ ...user, is_2fa_enabled: false })
        } catch (error) {
            toast.error(error.response?.data?.msg || 'Invalid verification code')
        } finally {
            setActionLoading(false)
        }
    }

    const copyToClipboard = (text, message) => {
        navigator.clipboard.writeText(text)
        toast.success(message)
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
        )
    }

    return (
        <div className="space-y-8">
            <div>
                <h2 className="text-2xl font-bold text-gray-900">Security Settings</h2>
                <p className="text-gray-500 mt-1">Manage your account security and authentication methods.</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Two-Factor Authentication Section */}
                <div className="p-6">
                    <div className="flex items-start justify-between">
                        <div className="flex gap-4">
                            <div className={`p-3 rounded-lg ${user?.is_2fa_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                                <Shield className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Two-Factor Authentication (2FA)</h3>
                                <p className="text-sm text-gray-500 mt-1 max-w-xl">
                                    Add an extra layer of security to your account by requiring more than just a password to log in.
                                </p>

                                {user?.is_2fa_enabled ? (
                                    <div className="mt-3 flex items-center text-sm font-medium text-green-600 bg-green-50 px-3 py-1.5 rounded-full w-fit">
                                        <CheckCircle2 className="w-4 h-4 mr-1.5" />
                                        Currently Enabled
                                    </div>
                                ) : (
                                    <div className="mt-3 flex items-center text-sm font-medium text-gray-500 bg-gray-50 px-3 py-1.5 rounded-full w-fit">
                                        <AlertTriangle className="w-4 h-4 mr-1.5" />
                                        Currently Disabled
                                    </div>
                                )}
                            </div>
                        </div>

                        <div>
                            {user?.is_2fa_enabled ? (
                                <Button
                                    variant="outline"
                                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                    onClick={() => {
                                        setDisableMode(true)
                                        setVerificationCode('')
                                    }}
                                    disabled={disableMode}
                                >
                                    Disable 2FA
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleStartSetup}
                                    disabled={setupMode || actionLoading}
                                >
                                    {actionLoading && !setupMode ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : 'Set Up 2FA'}
                                </Button>
                            )}
                        </div>
                    </div>

                    <AnimatePresence mode="wait">
                        {/* Disable 2FA Form */}
                        {disableMode && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-6 pt-6 border-t border-gray-100"
                            >
                                <div className="bg-red-50 border border-red-100 rounded-lg p-5">
                                    <h4 className="font-semibold text-red-800 mb-2 flex items-center">
                                        <AlertTriangle className="w-5 h-5 mr-2" />
                                        Disable Two-Factor Authentication
                                    </h4>
                                    <p className="text-sm text-red-700 mb-4">
                                        Disabling 2FA will make your account less secure. To confirm, please enter a code from your authenticator app.
                                    </p>

                                    <form onSubmit={handleDisable2FA} className="flex gap-3 max-w-md">
                                        <input
                                            type="text"
                                            value={verificationCode}
                                            onChange={(e) => setVerificationCode(e.target.value.replace(/\\D/g, '').slice(0, 6))}
                                            placeholder="6-digit code"
                                            className="flex-1 px-4 py-2 border border-red-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent block"
                                            maxLength={6}
                                        />
                                        <Button type="submit" variant="destructive" disabled={actionLoading || verificationCode.length !== 6}>
                                            {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : 'Confirm Disable'}
                                        </Button>
                                        <Button type="button" variant="outline" onClick={() => setDisableMode(false)}>
                                            Cancel
                                        </Button>
                                    </form>
                                </div>
                            </motion.div>
                        )}

                        {/* Setup 2FA Flow */}
                        {setupMode && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-6 pt-6 border-t border-gray-100"
                            >
                                {setupStep === 1 && (
                                    <div className="space-y-6">
                                        <div>
                                            <h4 className="font-semibold text-gray-900 text-lg">Step 1: Scan QR Code</h4>
                                            <p className="text-gray-500 text-sm mt-1">
                                                Use an authenticator app like Google Authenticator, Authy, or 1Password to scan this QR code.
                                            </p>
                                        </div>

                                        <div className="flex flex-col md:flex-row gap-8 items-center bg-gray-50 p-6 rounded-xl border border-gray-100">
                                            <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-200">
                                                {qrCodeData && (
                                                    <img src={qrCodeData} alt="2FA QR Code" className="w-48 h-48" />
                                                )}
                                            </div>

                                            <div className="space-y-4 flex-1">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-700 mb-1">Can't scan the QR code?</p>
                                                    <p className="text-xs text-gray-500 mb-2">Enter this setup key manually into your app instead.</p>
                                                    <div className="flex gap-2">
                                                        <code className="flex-1 bg-white px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono flex items-center overflow-x-auto">
                                                            {secretKey}
                                                        </code>
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={() => copyToClipboard(secretKey, 'Setup key copied')}
                                                            title="Copy setup key"
                                                        >
                                                            <Copy className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="border-t border-gray-100 pt-6">
                                            <h4 className="font-semibold text-gray-900 text-lg mb-3">Step 2: Verify Setup</h4>
                                            <p className="text-gray-500 text-sm mb-4">
                                                Enter the 6-digit code generated by your authenticator app to enable 2FA.
                                            </p>

                                            <form onSubmit={handleVerifySetup} className="flex gap-3 max-w-sm">
                                                <input
                                                    type="text"
                                                    value={verificationCode}
                                                    onChange={(e) => setVerificationCode(e.target.value.replace(/\\D/g, '').slice(0, 6))}
                                                    placeholder="000000"
                                                    className="flex-1 text-center font-mono text-lg tracking-wider px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                                    maxLength={6}
                                                />
                                                <Button type="submit" disabled={actionLoading || verificationCode.length !== 6}>
                                                    {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : 'Verify'}
                                                </Button>
                                            </form>
                                        </div>

                                        <div className="flex justify-end pt-4">
                                            <Button variant="ghost" className="text-gray-500" onClick={() => setSetupMode(false)}>
                                                Cancel Setup
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {setupStep === 2 && (
                                    <div className="space-y-6">
                                        <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                                            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                                <CheckCircle2 className="w-6 h-6 text-green-600" />
                                            </div>
                                            <h4 className="text-xl font-bold text-green-900 mb-2">2FA is now enabled!</h4>
                                            <p className="text-green-700 max-w-md mx-auto">
                                                Your account is now protected with two-factor authentication.
                                            </p>
                                        </div>

                                        <div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <h4 className="font-semibold text-gray-900 text-lg">Save Your Backup Codes</h4>
                                                <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded font-medium uppercase tracking-wider">Crucial</span>
                                            </div>
                                            <p className="text-gray-600 text-sm mb-4">
                                                If you lose access to your authenticator device, these codes are the ONLY way to access your account.
                                                <strong> Save them somewhere safe, like a password manager.</strong> Each code can only be used once.
                                            </p>

                                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                                                <div className="grid grid-cols-2 gap-x-8 gap-y-4 mb-6">
                                                    {backupCodes.map((code, index) => (
                                                        <div key={index} className="bg-white px-4 py-2 rounded font-mono text-center tracking-widest text-lg border border-gray-100 shadow-sm">
                                                            {code.substring(0, 4)} {code.substring(4)}
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="flex gap-3 justify-center">
                                                    <Button
                                                        variant="outline"
                                                        onClick={() => copyToClipboard(backupCodes.join('\\n'), 'Backup codes copied to clipboard')}
                                                        className="bg-white"
                                                    >
                                                        <Copy className="w-4 h-4 mr-2" />
                                                        Copy All
                                                    </Button>
                                                    <Button onClick={() => {
                                                        setSetupMode(false)
                                                        setVerificationCode('')
                                                    }}>
                                                        I have saved these safely
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    )
}
